// App.js
import './index.css';
import './styles/modern-dashboard.css';
import React, { useEffect, useState } from 'react';
import { ModalProvider } from './context/ModalContext';
import { LoadingProvider } from './context/LoadingContext';
import { ImpersonationProvider } from './context/ImpersonationContext';
import { DarkModeProvider } from './context/DarkModeContext';
import { Toaster } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import * as Sentry from "@sentry/react";
import MarketingRoutes from './MarketingRoutes';
import AppRoutes from './AppRoutes'; // ✅ now controls dashboard vs admin
import { supabase } from './utils/supabase';

Sentry.init({
  dsn: "https://e4e4170e47a3d8d9bbdacb71d59fb96e@o4509429646622720.ingest.de.sentry.io/4510018410381392",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration()
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Ignore errors from browser extensions and ad blockers
  ignoreErrors: [
    // Browser extension errors (password managers, etc.)
    /Object Not Found Matching Id/,
    /MethodName:update/,
    // Ad blocker blocking Stripe
    /Failed to load Stripe\.js/,
    // Common browser extension patterns
    /chrome-extension/,
    /moz-extension/,
    // ResizeObserver errors (usually benign)
    /ResizeObserver loop/,
  ],
  denyUrls: [
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    // Firefox extensions
    /^moz-extension:\/\//i,
  ],
});

function App() {
  const [isDashboardDomain, setIsDashboardDomain] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if it's a dashboard domain (my. subdomain) or Vercel preview URL
      const isVercelPreview = window.location.hostname.includes('vercel.app');
      setIsDashboardDomain(
        window.location.hostname.startsWith('my.') ||
        window.location.hostname.includes('.my.') ||
        isVercelPreview
      );

      // Handle "Remember Me" - check if we need to sign out on browser reopen
      // This only applies when user has an active session but browser was closed
      const checkRememberMe = async () => {
        // Skip this check on public routes (feedback forms, NPS, etc.)
        // These routes don't require auth and shouldn't trigger sign-out
        const publicRoutes = ['/feedback', '/nps', '/menu'];
        const isPublicRoute = publicRoutes.some(route => window.location.pathname.startsWith(route));
        if (isPublicRoute) return;

        const { data: { session } } = await supabase.auth.getSession();

        // Only proceed if there's an active session
        if (!session) return;

        const hadTempSession = sessionStorage.getItem('chatters_temp_session');
        const rememberMeEnabled = localStorage.getItem('chatters_remember_me') === 'true';

        // If user didn't want to be remembered and temp session flag is missing
        // (meaning browser was closed/reopened), sign them out
        if (!rememberMeEnabled && !hadTempSession) {
          await supabase.auth.signOut();
        }
      };

      checkRememberMe();

      const hash = window.location.hash;

      if (hash.includes('type=recovery') && !window.location.pathname.includes('/reset-password')) {
        const redirectUrl = `/reset-password${hash}`;
        window.location.replace(redirectUrl);
        return;
      }

      if (hash.includes('type=invite') && !window.location.pathname.includes('/set-password')) {
        const redirectUrl = `/set-password${hash}`;
        window.history.replaceState(null, '', redirectUrl);
      }
    }
  }, []);

  return (
    <DarkModeProvider>
      <div className={isDashboardDomain ? 'font-sans' : 'font-marketing'}>
        <LoadingProvider>
          <ImpersonationProvider>
            <ModalProvider>
              <Toaster
                position="bottom-right"
                toastOptions={{
                  duration: 2000,
                  success: {
                    style: {
                      background: '#22c55e',
                      color: '#fff',
                    },
                    iconTheme: {
                      primary: '#fff',
                      secondary: '#22c55e',
                    },
                  },
                  error: {
                    style: {
                      background: '#ef4444',
                      color: '#fff',
                    },
                    iconTheme: {
                      primary: '#fff',
                      secondary: '#ef4444',
                    },
                  },
                }}
              />
              <Sentry.ErrorBoundary fallback={<p>Something went wrong!</p>} showDialog>
                {isDashboardDomain ? (
                  <AppRoutes />
                ) : (
                  <MarketingRoutes />
                )}
              </Sentry.ErrorBoundary>
              <Analytics />
              <SpeedInsights />
            </ModalProvider>
          </ImpersonationProvider>
        </LoadingProvider>
      </div>
    </DarkModeProvider>
  );
}

export default App;
